import React, { useEffect, useState } from "react";
import { showApiErrorToast, showSuccessToast } from "../../../utils/toastUtils";
import { Plus, Pencil, Trash2 } from "lucide-react";

import ConfirmDialog from "../../../components/common/ConfirmDialog";
import FormButton from "../../../components/form/FormButton";
import TableToolbar from "../../../components/table/TableToolbar";
import Paginator from "../../../components/table/Paginator";
import useTableController from "../../../components/table/useTableController";

import PromoForm from "./PromoForm";
import {
  listPromos,
  createPromo,
  updatePromo,
  togglePromoStatus,
  deletePromo,
} from "./promoApi";

import "./promo.css";

const DiscountPill = ({ type }) => {
  return (
    <span className={`pill ${type === "percentage" ? "pill-venue" : "pill-listing"}`}>
      {type === "percentage" ? "Percentage" : "Flat"}
    </span>
  );
};

const StatusPill = ({ active }) => {
  return active ? (
    <span className="pill pill-active">Active</span>
  ) : (
    <span className="pill pill-inactive">Inactive</span>
  );
};

const PromosPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingItem, setDeletingItem] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [createFormError, setCreateFormError] = useState({});
  const [editFormError, setEditFormError] = useState({});

  const tc = useTableController(items, {
    searchKeys: ["code", "description", "discountType", "fundedBy", "userType"],
    initialPageSize: 10,
  });

  const load = async () => {
    setLoading(true);

    try {
      const data = await listPromos({ page: 1, limit: 100 });
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      showApiErrorToast(e, "Failed to load promo codes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreate = async (payload) => {
    try {
      setSubmitting(true);
      setCreateFormError({});

      await createPromo(payload);

      showSuccessToast("Promo created successfully");
      setCreateOpen(false);
      setCreateFormError({});
      await load();
    } catch (e) {
      const message = e?.response?.data?.message || "Failed to create promo code";

      if (message.toLowerCase().includes("promo code already exists")) {
        setCreateFormError({ code: message });
        return;
      }

      showApiErrorToast(e, "Failed to create promo");
      throw e;
    } finally {
      setSubmitting(false);
    }
  };

  const openCreate = () => {
    setCreateFormError({});
    setCreateOpen(true);
  };

  const openEdit = (item) => {
    setEditFormError({});
    setEditingItem(item);
  };

  const openDelete = (item) => {
    setDeletingItem(item);
    setDeleteOpen(true);
  };

  const handleUpdate = async (payload) => {
    try {
      const id = editingItem?._id || editingItem?.id;
      if (!id) return;

      setSubmitting(true);
      setEditFormError({});

      await updatePromo(id, payload);

      showSuccessToast("Promo updated successfully");
      setEditingItem(null);
      setEditFormError({});
      await load();
    } catch (e) {
      const message = e?.response?.data?.message || "Failed to update promo code";

      if (message.toLowerCase().includes("promo code already exists")) {
        setEditFormError({ code: message });
        return;
      }

      showApiErrorToast(e, "Failed to update promo");
      throw e;
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (item) => {
    const id = item?._id || item?.id;
    if (!id) return;

    try {
      setBusyId(id);

      await togglePromoStatus(id, !item.isActive);

      showSuccessToast(`Promo ${item.isActive ? "disabled" : "enabled"} successfully`);
      await load();
    } catch (e) {
      showApiErrorToast(e, "Failed to update promo status");
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async () => {
    const id = deletingItem?._id || deletingItem?.id;
    if (!id) return;

    try {
      setBusyId(id);

      await deletePromo(id);

      showSuccessToast("Promo deleted successfully");
      setDeleteOpen(false);
      setDeletingItem(null);
      await load();
    } catch (e) {
      showApiErrorToast(e, "Failed to delete promo");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <div className="sa-head-row">
        <h1 className="sa-h1 sa-topbar-managed-page-title">Promo Codes</h1>

        <FormButton variant="primary" className="promo-new-btn" onClick={openCreate}>
          <Plus className="lucide" /> New Promo
        </FormButton>
      </div>

      <TableToolbar
        searchTerm={tc.searchTerm}
        onSearchChange={tc.setSearch}
        filterDefs={[
          {
            id: "discountType",
            label: "Discount Type",
            options: [
              { value: "all", label: "All types" },
              { value: "flat", label: "Flat" },
              { value: "percentage", label: "Percentage" },
            ],
          },
          {
            id: "userType",
            label: "User Type",
            options: [
              { value: "all", label: "All users" },
              { value: "new", label: "New users" },
              { value: "specific", label: "Specific users" },
            ],
          },
        ]}
        filters={tc.filters}
        onFilterChange={tc.setFilter}
        searchPlaceholder="Search promo codes..."
      />

      <div className="sa-card">
        {loading ? (
          <div className="sa-placeholder" style={{ height: 120 }}>
            Loading…
          </div>
        ) : tc.total === 0 ? (
          <div className="sa-empty">
            <div>No promo codes found.</div>

            <FormButton variant="primary" onClick={openCreate}>
              Create your first promo
            </FormButton>
          </div>
        ) : (
          <div className="sa-table-wrap">
            <table className="sa-table">
              <thead>
                <tr>
                  <th style={{ width: 60 }}>S.N.</th>
                  <th>Code</th>
                  <th>Description</th>
                  <th>Discount</th>
                  <th>Value</th>
                  <th>Min Booking</th>
                  <th>User Type</th>
                  <th>Funded By</th>
                  <th>Status</th>
                  <th style={{ textAlign: "right", width: 220 }}>Actions</th>
                </tr>
              </thead>

              <tbody>
                {tc.rows.map((item, i) => {
                  const id = item._id || item.id;

                  return (
                    <tr key={id || `${item.code}-${i}`}>
                      <td>{(tc.page - 1) * tc.pageSize + i + 1}</td>

                      <td>
                        <strong>{item.code}</strong>
                      </td>

                      <td className="sa-ellipsis" title={item.description || ""}>
                        {item.description || "—"}
                      </td>

                      <td>
                        <DiscountPill type={item.discountType} />
                      </td>

                      <td>
                        {item.discountType === "percentage"
                          ? `${item.discountValue}%`
                          : `₹${item.discountValue}`}
                      </td>

                      <td>{item.minBookingAmount ?? 0}</td>

                      <td style={{ textTransform: "capitalize" }}>
                        {item.userType || "all"}
                      </td>

                      <td style={{ textTransform: "capitalize" }}>
                        {item.fundedBy || "platform"}
                      </td>

                      <td>
                        <StatusPill active={item.isActive} />
                      </td>

                      <td style={{ textAlign: "right" }}>
                        <div className="promo-row-actions">
                          <button
                            type="button"
                            className="sa-icon-link"
                            onClick={() => openEdit(item)}
                            title="Edit promo"
                            aria-label="Edit promo"
                          >
                            <Pencil className="lucide" />
                          </button>

                          <div className="sa-status-wrap promo-status-inline">
                            <label className="sa-switch" title="Toggle status">
                              <input
                                type="checkbox"
                                checked={!!item.isActive}
                                onChange={() => handleToggleStatus(item)}
                                disabled={busyId === id}
                              />
                              <span />
                            </label>

                            <span
                              className={`sa-status ${
                                item.isActive ? "active" : "inactive"
                              }`}
                            >
                              {item.isActive ? "Active" : "Inactive"}
                            </span>
                          </div>

                          <button
                            type="button"
                            className="sa-icon-link danger"
                            onClick={() => openDelete(item)}
                            disabled={busyId === id}
                            title="Delete promo"
                            aria-label="Delete promo"
                          >
                            <Trash2 className="lucide" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <Paginator
              page={tc.page}
              totalPages={tc.totalPages}
              onPage={tc.setPage}
              pageSize={tc.pageSize}
              onPageSizeChange={tc.setPageSize}
              pageSizeOptions={[5, 10, 20, 50]}
            />
          </div>
        )}
      </div>

      {createOpen && (
        <PromoForm
          open={createOpen}
          mode="create"
          externalErrors={createFormError}
          onExternalErrorChange={setCreateFormError}
          onCancel={() => {
            if (!submitting) {
              setCreateOpen(false);
              setCreateFormError({});
            }
          }}
          onSubmit={handleCreate}
          submitting={submitting}
        />
      )}

      {editingItem && (
        <PromoForm
          key={editingItem?._id || editingItem?.id}
          open={!!editingItem}
          mode="edit"
          initialData={editingItem}
          externalErrors={editFormError}
          onExternalErrorChange={setEditFormError}
          onCancel={() => {
            if (!submitting) {
              setEditingItem(null);
              setEditFormError({});
            }
          }}
          onSubmit={handleUpdate}
          submitting={submitting}
        />
      )}

      <ConfirmDialog
        open={deleteOpen}
        title="Delete Promo Code"
        message={`Are you sure you want to delete promo code "${
          deletingItem?.code || ""
        }"? This action cannot be undone.`}
        confirmText="Delete Promo"
        cancelText="Cancel"
        confirmVariant="danger"
        loading={busyId === (deletingItem?._id || deletingItem?.id)}
        onConfirm={handleDelete}
        onCancel={() => {
          if (busyId !== (deletingItem?._id || deletingItem?.id)) {
            setDeleteOpen(false);
            setDeletingItem(null);
          }
        }}
      />
    </>
  );
};

export default PromosPage;